import React, { useState, useEffect, useCallback } from 'react';
import './Addjob.css'; // Renamed CSS file
import AdminSidebar from '../Adminsidebar/AdminSidebar';

// SVG icon for delete button for a cleaner look
const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
);

const API_URL = 'https://api.hitkarihospital.com:5005/api';

const AddJobManagement = () => {
    const [jobRoles, setJobRoles] = useState([]);
    const [jobName, setJobName] = useState('');
    const [jobValue, setJobValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const fetchJobRoles = useCallback(async () => {
        // Show loader only on initial fetch
        if (jobRoles.length === 0) setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}/jobs`);
            if (!response.ok) throw new Error('Failed to fetch jobs.');
            const data = await response.json();
            setJobRoles(data);
        } catch (err) {
            setError(err.message);
        } finally {
             if (jobRoles.length === 0) setIsLoading(false);
        }
    }, [jobRoles.length]); // Dependency ensures it only shows loader on first run

    useEffect(() => {
        fetchJobRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    useEffect(() => {
        const generatedValue = jobName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        setJobValue(generatedValue);
    }, [jobName]);

    const handleAddJob = async (e) => {
        e.preventDefault();
        if (!jobName || !jobValue) {
            setError('Both Job Name and Job Value are required.');
            return;
        }
        setIsLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await fetch(`${API_URL}/jobs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: jobName, value: jobValue }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.msg || 'Failed to add job.');
            
            setSuccessMessage(`Successfully added "${result.name}"!`);
            setJobName('');
            await fetchJobRoles();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
            setTimeout(() => setSuccessMessage(''), 4000);
        }
    };

  const handleDeleteJob = async (jobId, jobName) => {
    const confirmed = window.confirm(`Are you sure you want to delete the job role: "${jobName}"?`);
    if (!confirmed) return;

    // Immediately update the UI for a snappy user experience
    const originalJobRoles = [...jobRoles];
    setJobRoles(prevRoles => prevRoles.filter(job => job._id !== jobId));
    
    setError('');
    setSuccessMessage('');

    try {
        const response = await fetch(`${API_URL}/jobs/${jobId}`, {
            method: 'DELETE',
        });

        const result = await response.json();

        if (!response.ok) {
            // If the delete fails, revert the UI to its original state and show error
            setJobRoles(originalJobRoles);
            throw new Error(result.msg || 'Failed to delete.');
        }

        // On success, just show the success message. The UI is already updated.
        setSuccessMessage(result.msg || 'Job role deleted successfully.');

    } catch (err) {
        // The error case already reverts the UI, so just show the message
        setError(err.message);
    } finally {
        // No need to set isLoading here unless you want to disable buttons during the API call
        setTimeout(() => setSuccessMessage(''), 4000);
    }
};

    return (
        <div className="ajm-layout">
            <AdminSidebar />
            <main className="ajm-panel-container">
                <header className="ajm-header">
                    <h1>Job Role Management</h1>
                    <p>Add, view, or remove job roles available on the career page.</p>
                </header>

                <div className="ajm-content-grid">
                    {/* Add Job Form Card */}
                    <div className="ajm-card ajm-form-card">
                        <h2 className="ajm-card-header">Add New Job Role</h2>
                        <form onSubmit={handleAddJob} noValidate>
                            <div className="ajm-form-group">
                                <label htmlFor="ajm-jobName">Job Name</label>
                                <input
                                    type="text"
                                    id="ajm-jobName"
                                    className="ajm-input"
                                    placeholder="e.g., Senior Frontend Developer"
                                    value={jobName}
                                    onChange={(e) => setJobName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="ajm-form-group">
                                <label htmlFor="ajm-jobValue">Job Value (auto-generated)</label>
                                <input
                                    type="text"
                                    id="ajm-jobValue"
                                    className="ajm-input"
                                    placeholder="e.g., senior-frontend-developer"
                                    value={jobValue}
                                    onChange={(e) => setJobValue(e.target.value)}
                                    required
                                />
                                <small className="ajm-form-hint">This unique ID is used in URLs and the database.</small>
                            </div>
                            <button type="submit" className="ajm-btn ajm-btn-primary" disabled={isLoading}>
                                {isLoading ? 'Adding...' : 'Add Job Role'}
                            </button>
                        </form>
                        {error && <p className="ajm-message ajm-error-msg">{error}</p>}
                        {successMessage && <p className="ajm-message ajm-success-msg">{successMessage}</p>}
                    </div>

                    {/* Existing Jobs List Card */}
                    <div className="ajm-card ajm-list-card">
                        <h2 className="ajm-card-header">Existing Job Roles</h2>
                        <ul className="ajm-job-list">
                            {jobRoles.length > 0 ? (
                                jobRoles.map((job) => (
                                    <li key={job._id} className="ajm-job-item">
                                        <div className="ajm-job-info">
                                            <span className="ajm-job-name">{job.name}</span>
                                            <span className="ajm-job-value">
                                                ID: <code>{job.value}</code>
                                            </span>
                                        </div>
                                        <button 
                                            className="ajm-btn ajm-btn-delete" 
                                            onClick={() => handleDeleteJob(job._id, job.name)}
                                            disabled={isLoading}
                                            title={`Delete ${job.name}`}
                                        >
                                            <TrashIcon />
                                        </button>
                                    </li>
                                ))
                            ) : (
                                <div className="ajm-empty-state">
                                    <p>No job roles found.</p>
                                    <span>Add one to get started!</span>
                                </div>
                            )}
                        </ul>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AddJobManagement;